import { useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Plus, X, GripVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Card, CardContent, CardHeader, CardTitle, CardDescription,
  Badge, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@higgins/ui';
import { useReports } from '@/lib/hooks';
import { MONTHS } from '@/lib/types';
import type { Report } from '@/lib/types';
import { createCombinedReport, fetchCombinedExport } from '@/lib/api';
import { SavedDecks } from '@/components/saved-decks';

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

// ─── Cover page fields ────────────────────────────────────────────────────────

interface CoverFields {
  title: string;
  date: string;
  preparedBy: string;
  intro: string;
}

// ─── Report picker ────────────────────────────────────────────────────────────

const ALL = '__ALL__';

function ReportPicker({ reports, selected, onAdd, period }: {
  reports: Report[];
  selected: string[];
  onAdd: (id: string) => void;
  period: { month: number; year: number } | null;
}) {
  const [regionFilter, setRegionFilter] = useState<string>(ALL);
  const [branchFilter, setBranchFilter] = useState<string>(ALL);

  // The deck's month is chosen explicitly on the cover page. Until it's set,
  // there is nothing to pick from.
  if (!period) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        Choose a report month on the cover page to see the reports you can add.
      </p>
    );
  }

  // Published and Distributed reports for the chosen period that aren't already
  // added. Both carry a finalised snapshot — distributing a deck flips its reports
  // to Distributed, but they must stay available for re-use in new decks.
  const periodAvailable = reports
    .filter(r => (r.status === 'Published' || r.status === 'Distributed') && !selected.includes(r.id))
    .filter(r => r.year === period.year && r.month === period.month);

  const regionOptions = Array.from(new Set(periodAvailable.map(r => r.region))).sort();
  const branchOptions = Array.from(new Set(periodAvailable.flatMap(r => r.branches))).sort();

  const available = periodAvailable
    .filter(r => regionFilter === ALL || r.region === regionFilter)
    .filter(r => branchFilter === ALL || r.branches.includes(branchFilter))
    .sort((a, b) => a.region.localeCompare(b.region));

  return (
    <div className="space-y-3">
      {/* Filters — month is fixed by the cover-page period; refine by region/branch */}
      <div className="grid grid-cols-2 gap-2">
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All regions</SelectItem>
            {regionOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Branch" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All branches</SelectItem>
            {branchOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Results */}
      {available.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          {periodAvailable.length === 0
            ? (selected.length ? 'All reports for this month are already added.' : 'No published or distributed reports for this month.')
            : 'No reports match the current filters.'}
        </p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {available.map(r => (
            <div key={r.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
              <div className="text-sm">
                <span className="font-medium">{r.region}</span>
                <span className="text-muted-foreground"> · {MONTHS[r.month - 1]} {r.year}</span>
                <span className="text-muted-foreground"> · {r.branches.join(', ')}</span>
                {r.status === 'Distributed' && (
                  <Badge variant="outline" className="ml-2 text-[10px] py-0">Distributed</Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => onAdd(r.id)} className="h-6 px-2">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CombinedPage() {
  const navigate  = useNavigate();
  const queryClient = useQueryClient();
  const { data: reports = [] } = useReports();

  const [selected, setSelected] = useState<string[]>([]);
  const [period, setPeriod] = useState<{ month: number; year: number } | null>(null);
  const [cover, setCover] = useState<CoverFields>({
    title: 'GM Monthly Report',
    date: new Date().toISOString().slice(0, 10),
    preparedBy: '',
    intro: '',
  });
  const [generating, setGenerating] = useState(false);

  // Distinct months that actually have a finalised (Published/Distributed) report,
  // each with a count — this drives the explicit "Report month" dropdown.
  const periodOptions = Array.from(
    reports
      .filter(r => r.status === 'Published' || r.status === 'Distributed')
      .reduce((m, r) => {
        const key = `${r.year}-${r.month}`;
        m.set(key, (m.get(key) ?? 0) + 1);
        return m;
      }, new Map<string, number>())
      .entries(),
  )
    .map(([key, count]) => {
      const [year, month] = key.split('-').map(Number);
      return { key, year, month, count };
    })
    .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month));

  function handlePeriodChange(value: string) {
    const [year, month] = value.split('-').map(Number);
    const changed = !period || period.year !== year || period.month !== month;
    setPeriod({ month, year });
    if (changed && selected.length) {
      setSelected([]);
      toast.info('Selection cleared', { description: 'Report month changed — pick reports for the new month.' });
    }
  }

  function addReport(id: string) {
    setSelected(prev => prev.includes(id) ? prev : [...prev, id]);
  }

  function removeReport(id: string) {
    setSelected(prev => prev.filter(x => x !== id));
  }

  function moveReport(fromIdx: number, toIdx: number) {
    setSelected(prev => {
      const next = [...prev];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      return next;
    });
  }

  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  function onDragStart(i: number) { setDraggingIdx(i); }

  function onDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (draggingIdx === null || draggingIdx === i) return;
    moveReport(draggingIdx, i);
    setDraggingIdx(i);
  }

  const selectedReports = selected
    .map(id => reports.find(r => r.id === id))
    .filter((r): r is Report => !!r);

  // The deck title shown on the cover / used for the filename is the typed base
  // title with the chosen period appended, e.g. "GM Monthly Report — June 2026".
  const periodLabel = period ? `${MONTHS[period.month - 1]} ${period.year}` : '';
  const baseTitle = cover.title.trim() || 'GM Monthly Report';
  const effectiveTitle = period ? `${baseTitle} — ${periodLabel}` : baseTitle;

  async function handleGenerate() {
    if (!period) {
      toast.error('No report month chosen', { description: 'Select the report month on the cover page first.' });
      return;
    }
    if (!selected.length) {
      toast.error('No reports selected', { description: 'Add at least one report to the combined deck.' });
      return;
    }
    // Defensive: every report in the deck must match the chosen month and year.
    const mismatched = selectedReports.some(r => r.month !== period.month || r.year !== period.year);
    if (mismatched) {
      toast.error('Reports span multiple months', { description: 'A combined deck must cover a single month.' });
      return;
    }
    if (IS_MOCK) {
      toast.info('Combined export (mock)', { description: 'Not available in demo mode.' });
      return;
    }
    setGenerating(true);
    try {
      const { id } = await createCombinedReport({
        title:      effectiveTitle,
        date:       cover.date,
        preparedBy: cover.preparedBy,
        intro:      cover.intro,
        reportIds:  selected,
      });

      const { url } = await fetchCombinedExport(id);
      const filename = `${effectiveTitle} - ${cover.date}.docx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Combined report ready', { description: 'Your Word document is downloading. It now appears in Saved decks below.' });
      // Reset the builder and refresh the saved-decks list in place.
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ['combined'] });
    } catch (e: any) {
      toast.error('Combined export failed', { description: e?.message ?? 'Please try again.' });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Dashboard
        </Button>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Combined Report Builder</h2>
          <p className="text-sm text-muted-foreground">Assemble multiple GM reports into a single deck.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: cover + generate */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cover page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Report month</Label>
                <Select value={period ? `${period.year}-${period.month}` : ''} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select a month…" /></SelectTrigger>
                  <SelectContent>
                    {periodOptions.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">No published reports yet</div>
                    ) : periodOptions.map(o => (
                      <SelectItem key={o.key} value={o.key}>
                        {MONTHS[o.month - 1]} {o.year} ({o.count} report{o.count === 1 ? '' : 's'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Every report in the deck must be from this month.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Title</Label>
                <Input
                  value={cover.title}
                  onChange={e => setCover(c => ({ ...c, title: e.target.value }))}
                  placeholder="GM Monthly Report"
                />
                {period && (
                  <p className="text-[11px] text-muted-foreground">
                    Deck title: <span className="font-medium text-slate-600">{effectiveTitle}</span>
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={cover.date}
                  onChange={e => setCover(c => ({ ...c, date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prepared by</Label>
                <Input
                  value={cover.preparedBy}
                  onChange={e => setCover(c => ({ ...c, preparedBy: e.target.value }))}
                  placeholder="Your name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Introduction</Label>
                <textarea
                  value={cover.intro}
                  onChange={e => setCover(c => ({ ...c, intro: e.target.value }))}
                  placeholder="Optional introductory text…"
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <Button
                className="w-full"
                onClick={() => handleGenerate()}
                disabled={generating || !period || !selected.length}
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download Word
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: report selection */}
        <div className="lg:col-span-2 space-y-4">
          {/* Selected reports (drag to reorder) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Selected reports
                {selectedReports.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{selectedReports.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>Drag rows to change the order in the combined output.</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {selectedReports.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No reports added yet — pick from the list below.
                </p>
              ) : (
                <div className="space-y-1">
                  {selectedReports.map((r, i) => (
                    <div
                      key={r.id}
                      draggable
                      onDragStart={() => onDragStart(i)}
                      onDragOver={e => onDragOver(e, i)}
                      onDragEnd={() => setDraggingIdx(null)}
                      className={`flex items-center gap-3 px-2 py-2 rounded cursor-grab active:cursor-grabbing transition-colors ${
                        draggingIdx === i ? 'bg-accent opacity-50' : 'hover:bg-muted/50'
                      }`}
                    >
                      <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 text-sm">
                        <span className="font-medium">{r.region}</span>
                        <span className="text-muted-foreground"> · {MONTHS[r.month - 1]} {r.year}</span>
                        <span className="text-muted-foreground"> · {r.branches.join(', ')}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">#{i + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => removeReport(r.id)}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available reports picker */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Available reports</CardTitle>
              <CardDescription>
                {period
                  ? `Published and distributed reports for ${periodLabel}.`
                  : 'Choose a report month on the cover page to begin.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <ReportPicker
                reports={reports}
                selected={selected}
                onAdd={addReport}
                period={period}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Saved decks — download or distribute previously built combined reports */}
      <SavedDecks />
    </motion.div>
  );
}
